import { canBuyGiftCard } from "utils/giftCardAvailability";
import { useNavigate } from "react-router-dom";

// elements/button is the styled Button used across the app - components/Button
// is an older one whose classes are not picked up by the global button styles.
import { Button } from "elements/button/Button";
import { COLORS } from "models/colors";
import { VARIANTS } from "models/variants";

import { useUser } from "providers/user";
import { useCognito } from "providers/cognito";
import { hasPendingGiftCardPurchase } from "utils/giftCardPurchaseRequest";

import "./GiftCardPromo.scss";
import { SvgIcon } from "elements/svg-icon/svg-icon";

// The "Buy a Gift Card" block. Appears on both the company page and each
// location page - a customer who lands directly on a salon's location should
// be able to buy one without first finding the company page.
//
// Rendered only when the salon has gift cards switched on AND has opened
// online sales. Both flags come from the company record; the server checks
// them again on purchase, this is just so we do not show a button that leads
// somewhere it cannot work.

type Props = {
  companyId?: string | null;
  companySlug?: string | null;
  companyName?: string | null;
  companyStatus?: string | null;
  stripeAccountId?: string | null;
  giftCardsEnabled?: boolean | null;
  giftCardsOnlineEnabled?: boolean | null;
  showModal: boolean;
  setShowModal: (show: boolean) => void;
};

export function GiftCardPromo({
  companyId,
  companySlug,
  companyName,
  giftCardsEnabled,
  giftCardsOnlineEnabled,
  companyStatus,
  stripeAccountId,
  setShowModal
}: Props) {
  const navigate = useNavigate();
  const { user } = useUser();
  const { user: cognitoUser } = useCognito();
  const onlineSalesEnabled = canBuyGiftCard({
    status: companyStatus,
    stripe_user_id: stripeAccountId,
    gift_cards_enabled: giftCardsEnabled,
    gift_cards_online_enabled: giftCardsOnlineEnabled
  });
  const hasPendingPurchase = hasPendingGiftCardPurchase(
    cognitoUser?.id,
    companyId
  );

  if ((!onlineSalesEnabled && !hasPendingPurchase) || !companySlug) {
    return null;
  }

  return (
    <div className="gift-card-promo">
      <div className="gift-card-promo__text">
        <SvgIcon name="gift_card" className="gift-card-promo__card-icon" />
        <p className="gift-card-promo__title">
          {hasPendingPurchase
            ? "Continue your gift card purchase"
            : `Give the gift of ${companyName || "this salon"}`}
        </p>
      </div>
      <Button
        color={COLORS.PRIMARY}
        variant={VARIANTS.FILLED}
        text={hasPendingPurchase ? "Check purchase" : "Buy a Gift Card"}
        // `flexible` makes the button's inner state-layer (which carries the
        // background) fill the button, which matters on mobile where the scss
        // stretches it to full width.
        className="gift-card-promo__button flexible !h-11"
        onClick={() => {
          // Buying needs an account. Signed-out visitors go to login first,
          // which returns them here, rather than opening a modal they cannot
          // complete.
          if (!user?.id) {
            navigate("/login", {
              state: { from: { pathname: window.location.pathname } }
            });
            return;
          }
          setShowModal(true);
        }}
      />
    </div>
  );
}

export default GiftCardPromo;
